import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ScanFace, CarFront, ShieldCheck, ChevronLeft, ChevronRight, Video } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const SLIDE_SRCS = ["/case/camera-1.jpg", "/case/camera-2.jpg", "/case/camera-3.jpg", "/case/camera-4.jpg"];

const TECH_ICONS = { "speed-dome": ScanFace, lpr: CarFront, integracao: ShieldCheck };

const CaseSection = () => {
  const { t } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    const timer = setInterval(() => emblaApi.scrollNext(), 4500);
    return () => clearInterval(timer);
  }, [emblaApi]);

  return (
    <section id="case" data-testid="case-section" className="overflow-hidden bg-[#0F2117] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
          <Reveal>
            <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-emerald-400">
              {t.case.eyebrow}
            </p>
            <h2 className="font-display mt-4 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {t.case.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="justify-self-start lg:justify-self-end">
            <div
              data-testid="case-pmerj-badge"
              className="flex items-center gap-4 rounded-3xl border border-emerald-500/20 bg-[#07120C]/70 p-4 pr-6"
            >
              <img
                src="/case/pmerj.jpg"
                alt={t.case.pmerjAlt}
                className="h-20 w-auto rounded-xl"
              />
              <div>
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-emerald-400">
                  {t.case.pmerjEyebrow}
                </p>
                <p className="font-display mt-1 max-w-[180px] text-sm font-semibold leading-snug text-white">
                  {t.case.pmerjName}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-400 sm:text-lg">
            {t.case.objective}
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {t.case.techs.map((tech, i) => {
            const Icon = TECH_ICONS[tech.id];
            return (
              <Reveal key={tech.id} delay={i * 0.12}>
                <article
                  data-testid={`case-tech-${tech.id}`}
                  className="group h-full rounded-3xl border border-emerald-500/20 bg-[#07120C]/70 p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-emerald-400/50 hover:shadow-xl hover:shadow-emerald-500/10"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/15 transition-colors duration-300 group-hover:bg-emerald-500/25">
                    <Icon className="h-6 w-6 text-emerald-400" />
                  </span>
                  <h3 className="font-display mt-5 text-lg font-semibold leading-snug text-white">
                    {tech.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{tech.desc}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Reveal delay={0.1} className="mt-16">
        <div className="relative" data-testid="case-carousel">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {SLIDE_SRCS.map((src, i) => (
                <div
                  key={src}
                  className="relative min-w-0 shrink-0 grow-0 basis-[85%] px-3 sm:basis-[60%] lg:basis-[45%]"
                >
                  <div
                    className={`relative overflow-hidden rounded-3xl border transition-all duration-700 ${
                      selected === i
                        ? "scale-100 border-emerald-400/60 opacity-100 shadow-2xl shadow-emerald-500/20"
                        : "scale-95 border-emerald-500/10 opacity-40"
                    }`}
                  >
                    <img
                      src={src}
                      alt={t.case.slides[i].alt}
                      loading="lazy"
                      className="h-[24rem] w-full object-cover sm:h-[28rem]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07120C]/90 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 p-6">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/90">
                        <Video className="h-4 w-4 text-[#07120C]" />
                      </span>
                      <p className="font-display text-sm font-semibold text-white sm:text-base">
                        {t.case.slides[i].caption}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              data-testid="case-carousel-prev"
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              className="rounded-full border border-emerald-500/30 p-3 text-emerald-300 transition-colors duration-300 hover:border-emerald-400/70 hover:text-emerald-100"
              aria-label={t.case.prev}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-2.5" data-testid="case-carousel-dots">
              {SLIDE_SRCS.map((src, i) => (
                <button
                  key={src}
                  data-testid={`case-carousel-dot-${i}`}
                  onClick={() => emblaApi && emblaApi.scrollTo(i)}
                  aria-label={`${t.case.goTo} ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    selected === i ? "w-8 bg-emerald-400" : "w-2 bg-emerald-500/30 hover:bg-emerald-500/60"
                  }`}
                />
              ))}
            </div>
            <button
              data-testid="case-carousel-next"
              onClick={() => emblaApi && emblaApi.scrollNext()}
              className="rounded-full border border-emerald-500/30 p-3 text-emerald-300 transition-colors duration-300 hover:border-emerald-400/70 hover:text-emerald-100"
              aria-label={t.case.next}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default CaseSection;
